import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { ComponentType } from "react";
import { FcGoogle } from "react-icons/fc";

import { isAllowedRedirectUrl } from "@shared/auth";
import { Button } from "@shared/elements";

import LastUsedBadge from "src/components/LastUsedBadge";

import { MESSAGE_PREFIX, paths } from "src/constants";
import {
  SIGN_IN_PARAMS,
  SignInProvider,
  createSignInState,
} from "src/lib/sign-in-state";

import MicrosoftIcon from "./MicrosoftIcon";

export { SIGN_IN_PARAMS } from "src/lib/sign-in-state";

/**
 * What differs between providers: the core-api route that runs the dance,
 * and the mark on the button. Everything else is shared.
 */
const PROVIDERS: Record<
  SignInProvider,
  { path: string; Icon: ComponentType<{ className?: string }> }
> = {
  google: { path: "/auth/google", Icon: FcGoogle },
  microsoft: { path: "/auth/microsoft", Icon: MicrosoftIcon },
};

/**
 * Starts the "sign in with <provider>" flow served by core-api.
 *
 * The API route (`GET /auth/<provider>`) runs the whole OAuth dance itself,
 * sets the `jwt` cookie and finally redirects the browser to `next`. `next`
 * has to be an absolute URL the API allowlists, so it is built from the
 * current origin at click time — it is not known while the page is rendered
 * statically.
 *
 * `next` is always this app's /welcome page. It carries the page's
 * `redirectTo` (where the user wanted to go, like the password login does)
 * plus what the user answered next to the button (the newsletter checkbox on
 * the signup page) and a one-time token this tab remembers, so that only a
 * landing that really followed this click gets to act on the answer. The
 * welcome page applies the answer, asks when there was none, and forwards.
 * Without the token (storage unavailable) only `redirectTo` is sent.
 */
export default function ProviderLoginButton({
  provider,
  className = "w-full",
  newsletter,
  lastUsed = false,
}: {
  provider: SignInProvider;
  className?: string;
  /** This is the method the browser last signed in with: say so on the button. */
  lastUsed?: boolean;
  /**
   * The newsletter checkbox next to the button, when the page shows one.
   * Leave undefined when there is no checkbox, so the welcome page knows the
   * question was never asked.
   */
  newsletter?: boolean;
}) {
  const router = useRouter();
  const t = useTranslations(`${MESSAGE_PREFIX}components.ProviderLoginButton`);
  const { path, Icon } = PROVIDERS[provider];

  const onClick = () => {
    const state = createSignInState();
    const followUp = state
      ? {
          [SIGN_IN_PARAMS.via]: provider,
          [SIGN_IN_PARAMS.state]: state,
          ...(newsletter !== undefined
            ? { [SIGN_IN_PARAMS.newsletter]: newsletter ? "1" : "0" }
            : {}),
        }
      : {};
    const next = resolveReturnUrl(
      router.query.redirectTo,
      window.location.origin,
      {
        // Where to come back to with the reason, should the sign-in be refused.
        // The page with its own query: the submission login needs its callId.
        [SIGN_IN_PARAMS.from]: router.asPath,
        ...followUp,
      },
    );
    const params = new URLSearchParams({ next });

    window.location.assign(
      `${process.env.NEXT_PUBLIC_API_URL}${path}?${params.toString()}`,
    );
  };

  return (
    <div className="relative">
      <Button
        type="button"
        variant="default"
        className={className}
        onClick={onClick}
      >
        <Icon className="h-5 w-5 mr-2" />
        {t(provider)}
      </Button>
      {lastUsed && <LastUsedBadge />}
    </div>
  );
}

ProviderLoginButton.messages = [
  `${MESSAGE_PREFIX}components.ProviderLoginButton`,
  ...LastUsedBadge.messages,
];

/**
 * The absolute URL the API sends the user back to: this app's /welcome page,
 * with `redirectTo` set to where the user goes afterwards and `params` (the
 * follow-up contract) on the same query.
 *
 * `redirectTo` is kept when it is a path on `origin` or an absolute URL on one
 * of our own domains (the submission wizard hands its users to
 * /submission-login and expects them back), /home otherwise. A leading slash
 * is not enough of a check: `//host` and `/\host` both resolve to another
 * origin, so the candidate is parsed and its origin compared. An absolute URL
 * has to pass the same domain rule the password login applies.
 */
export function resolveReturnUrl(
  requested: string | string[] | undefined,
  origin: string,
  params: Record<string, string> = {},
): string {
  const destination = resolveRedirectTo(requested, origin);
  const welcome = new URL(paths.welcome, origin);

  welcome.searchParams.set(
    "redirectTo",
    destination.origin === origin
      ? `${destination.pathname}${destination.search}${destination.hash}`
      : destination.toString(),
  );
  for (const [key, value] of Object.entries(params)) {
    welcome.searchParams.set(key, value);
  }

  return welcome.toString();
}

function resolveRedirectTo(
  requested: string | string[] | undefined,
  origin: string,
): URL {
  if (typeof requested === "string") {
    try {
      const url = new URL(requested, origin);
      const isAbsolute = /^https?:\/\//.test(requested);

      if (
        url.origin === origin ||
        (isAbsolute && isAllowedRedirectUrl(requested))
      ) {
        return url;
      }
    } catch {
      // not a URL at all: fall through to the default
    }
  }

  return new URL(paths.home, origin);
}
